import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { HelpCircle, Info } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

interface InfoTooltipProps {
  /** Tooltip body text — already translated by the caller. */
  content: string;
  /** Accessible label for the trigger button — already translated; defaults to `content`. */
  ariaLabel?: string;
  /** Optional extra classes for the trigger button. */
  className?: string;
  /**
   * Which glyph to show. `'info'` (default) keeps every existing caller
   * unchanged; `'help'` is the question mark, for forms where the icon answers
   * "what does this field accept" rather than annotating a statistic.
   */
  icon?: 'info' | 'help';
  /**
   * Hover delay in ms. Omitted, the provider's default applies — Radix ships
   * 700ms, which on a 16px target reads as the tooltip being broken rather than
   * delayed. Dense forms pass something shorter.
   */
  delayDuration?: number | undefined;
  /** Automation hook: trigger button id; the tooltip body gets `${testId}-content`. */
  testId?: string;
}

/**
 * Small info-icon button that reveals an arrow-pointed tooltip on hover/focus.
 * Shared so the many "(i) beside a heading" sites stay visually + a11y
 * consistent instead of each re-implementing the trigger/icon/content markup.
 */
export function InfoTooltip({
  content,
  ariaLabel,
  className,
  icon = 'info',
  delayDuration,
  testId = 'info-tooltip',
}: InfoTooltipProps) {
  const Glyph = icon === 'help' ? HelpCircle : Info;
  return (
    // `delayDuration` is only forwarded when given, so an omitted prop still
    // inherits the provider's value rather than pinning it to a local default.
    <Tooltip {...(delayDuration !== undefined && { delayDuration })}>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label={ariaLabel ?? content}
          className={cn(
            'text-muted-foreground hover:text-foreground inline-flex cursor-help',
            className,
          )}
        >
          <Glyph className="h-4 w-4" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs" data-testid={`${testId}-content`}>
        {content}
        <TooltipPrimitive.Arrow className="fill-popover" />
      </TooltipContent>
    </Tooltip>
  );
}
